import { Database, Plus } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useState, useEffect } from "react";
import { Tables, Json } from "@/integrations/supabase/types";
import { useToast } from "@/hooks/use-toast";
import { CollectionControl } from "./source-management/CollectionControl";
import { SourceSettings } from "./source-management/SourceSettings";
import { ContentSourceConfig, CollectionStateConfig } from "./types";

type ContentSource = Tables<"content_sources">;

export const SourcesTab = () => {
  const [newSourceUrl, setNewSourceUrl] = useState("");
  const [isCollecting, setIsCollecting] = useState(false);
  const [autoCollection, setAutoCollection] = useState(false);
  const [minRelevanceScore, setMinRelevanceScore] = useState(0.5);
  const { toast } = useToast();

  // Fetch initial collection state
  useEffect(() => {
    const fetchCollectionState = async () => {
      const { data, error } = await supabase
        .from('content_source_configs')
        .select('config')
        .eq('source_type', 'collection_state')
        .maybeSingle();

      if (!error && data?.config) {
        const config = data.config as unknown as CollectionStateConfig;
        setIsCollecting(config.is_collecting || false);
      }
    };

    fetchCollectionState();
  }, []);

  // Fetch initial settings
  useEffect(() => {
    const fetchSettings = async () => {
      const { data, error } = await supabase
        .from('content_source_configs')
        .select('*')
        .eq('source_type', 'auto_collection')
        .single();

      if (data && typeof data.config === 'object' && data.config !== null) {
        const config = data.config as unknown as ContentSourceConfig;
        setAutoCollection(config.enabled ?? false);
        setMinRelevanceScore(config.min_relevance_score ?? 0.5);
      }
    };

    fetchSettings();
  }, []);

  const { data: sources, isLoading } = useQuery({
    queryKey: ['content-sources'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('content_sources')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as ContentSource[];
    },
  });

  const handleAddSource = async () => {
    if (!newSourceUrl.trim()) return;

    const { error } = await supabase
      .from('content_sources')
      .insert([
        { url: newSourceUrl, status: 'active' }
      ]);

    if (!error) {
      setNewSourceUrl("");
      toast({
        title: "Source Added",
        description: "New source has been added successfully"
      });
    }
  };

  const handleToggleCollection = async () => {
    try {
      // Update collection state in database
      const { error: configError } = await supabase
        .from('content_source_configs')
        .upsert({
          source_type: 'collection_state',
          config: { is_collecting: !isCollecting } as unknown as Json
        }, {
          onConflict: 'source_type'
        });

      if (configError) throw configError;

      // Call the source-gatherer function
      await supabase.functions.invoke('source-gatherer', {
        body: { 
          action: isCollecting ? 'STOP_COLLECTION' : 'START_COLLECTION',
          autoCollection
        }
      });

      setIsCollecting(!isCollecting);
      toast({
        title: isCollecting ? "Collection Stopped" : "Collection Started",
        description: isCollecting 
          ? "Source collection has been stopped" 
          : "Started collecting data from sources"
      });
    } catch (error) {
      console.error('Collection toggle error:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to toggle collection status"
      });
    }
  };

  const handleAutoCollectionToggle = async (enabled: boolean) => {
    try {
      const configData = {
        enabled,
        min_relevance_score: minRelevanceScore
      } as const;

      const { error } = await supabase
        .from('content_source_configs')
        .upsert({
          source_type: 'auto_collection',
          config: configData as unknown as Json
        }, {
          onConflict: 'source_type'
        });

      if (error) throw error;

      setAutoCollection(enabled);
      toast({
        title: "Auto-collection Updated",
        description: `Auto-collection has been ${enabled ? 'enabled' : 'disabled'}`
      });
    } catch (error) {
      console.error('Auto-collection toggle error:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update auto-collection settings"
      });
    }
  };

  const updateFilteringRules = async () => {
    try {
      const configData = {
        enabled: autoCollection,
        min_relevance_score: minRelevanceScore
      } as const;

      const { error } = await supabase
        .from('content_source_configs')
        .upsert({
          source_type: 'filtering_rules',
          config: configData as unknown as Json
        }, {
          onConflict: 'source_type'
        });

      if (error) throw error;

      toast({
        title: "Filtering Rules Updated",
        description: "Content filtering rules have been updated"
      });
    } catch (error) {
      console.error('Filtering rules update error:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update filtering rules"
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Database className="h-5 w-5 text-primary" />
        <h2 className="text-2xl font-bold tracking-tight">Content Sources</h2>
      </div>
      <p className="text-muted-foreground">
        Manage and monitor your content sources and data collection.
      </p>
      
      <Separator />
      
      <Tabs defaultValue="sources" className="space-y-4">
        <TabsList>
          <TabsTrigger value="sources">All Sources</TabsTrigger>
          <TabsTrigger value="add">Add Source</TabsTrigger>
          <TabsTrigger value="collection">Collection</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="sources">
          <Card>
            <CardHeader>
              <CardTitle>Active Sources</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div>Loading sources...</div>
              ) : (
                <div className="space-y-4">
                  {sources?.map((source) => (
                    <div
                      key={source.id}
                      className="flex items-center justify-between p-4 border rounded-lg"
                    >
                      <div>
                        <p className="font-medium">{source.url}</p>
                        <p className="text-sm text-muted-foreground">
                          Last collected: {new Date(source.last_collected_at || '').toLocaleString()}
                        </p>
                      </div>
                      <Badge
                        variant={source.status === 'active' ? 'default' : 'secondary'}
                      >
                        {source.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="add">
          <Card>
            <CardHeader>
              <CardTitle>Add New Source</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2">
                <Input
                  placeholder="Enter source URL"
                  value={newSourceUrl}
                  onChange={(e) => setNewSourceUrl(e.target.value)}
                />
                <Button onClick={handleAddSource}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Source
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="collection">
          <CollectionControl 
            isCollecting={isCollecting}
            onToggleCollection={handleToggleCollection}
          />
        </TabsContent>

        <TabsContent value="settings">
          <SourceSettings 
            autoCollection={autoCollection}
            minRelevanceScore={minRelevanceScore}
            onAutoCollectionToggle={handleAutoCollectionToggle}
            onMinRelevanceScoreChange={setMinRelevanceScore}
            onSaveRules={updateFilteringRules}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};
